/**
 * Axios 인스턴스 설정
 * - 요청 인터셉터: 만료/만료 임박 시 갱신 후 요청
 * - 응답 인터셉터: 401 시 갱신 시도
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import env from '../config/env';
import logger from '../services/logger';
import {
  getAuthTokenCookie,
  deleteAuthTokenCookie,
  getRefreshTokenStorage,
  deleteRefreshTokenStorage,
  getOrCreateDeviceId,
  setAuthTokenCookie,
} from 'core/utils/cookie';
import { getSecondsUntilExpiry, isTokenExpired } from 'core/utils/jwt';

/** 갱신 시도 시점: 만료 N초 전 */
const REFRESH_BUFFER_SECONDS = 300; // 5분

// Axios 인스턴스 생성
const apiClient: AxiosInstance = axios.create({
  baseURL: env.REACT_APP_API_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 토큰 갱신 중 플래그 (동시 요청 시 한 번만 refresh 시도)
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const callRefreshApi = async (): Promise<string | null> => {
  const refreshToken = getRefreshTokenStorage();
  if (!refreshToken) {
    return null;
  }
  const deviceId = getOrCreateDeviceId();
  const baseURL = env.REACT_APP_API_URL || '/api';
  const refreshUrl = `${baseURL}/auth/refresh`;
  logger.info('[토큰 갱신] POST /api/auth/refresh 호출 시도', {
    url: refreshUrl,
    hasRefreshToken: !!refreshToken,
    deviceId,
  });
  try {
    const response = await axios.post<{ access_token: string; expires_in: number }>(
      refreshUrl,
      { refresh_token: refreshToken, device_id: deviceId },
      {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        timeout: 10000,
      }
    );
    const { access_token, expires_in } = response.data;
    const expiresInHours = expires_in ? expires_in / 3600 : 2;
    setAuthTokenCookie(access_token, expiresInHours);
    logger.info('[토큰 갱신] 성공', { expiresIn: expires_in });
    return access_token;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    const status = (err as { response?: { status?: number } })?.response?.status;
    logger.warn('[토큰 갱신] API 실패', { message: msg, status });
    throw err;
  }
};

/** 갱신 실행 (동시 요청 시 한 번만 호출) */
const doRefreshWithLock = (): Promise<string | null> => {
  if (isRefreshing && refreshPromise) {
    logger.info('[토큰 갱신] 이미 갱신 진행 중, 대기');
    return refreshPromise;
  }
  isRefreshing = true;
  logger.info('[토큰 갱신] 갱신 시작');
  refreshPromise = callRefreshApi()
    .then((newToken) => {
      isRefreshing = false;
      refreshPromise = null;
      if (newToken) {
        logger.info('[토큰 갱신] 갱신 완료');
      }
      return newToken;
    })
    .catch((err) => {
      isRefreshing = false;
      refreshPromise = null;
      logger.warn('[토큰 갱신] 갱신 실패, null 반환', { error: err instanceof Error ? err.message : String(err) });
      return null;
    });
  return refreshPromise;
};

/** 만료/만료 임박 시 갱신, 유효하면 현재 토큰 반환 */
const ensureValidToken = async (): Promise<string | null> => {
  const token = getAuthTokenCookie();
  const needsRefresh =
    !token ||
    isTokenExpired(token) ||
    (getSecondsUntilExpiry(token) !== null && (getSecondsUntilExpiry(token) as number) <= REFRESH_BUFFER_SECONDS);
  if (!needsRefresh) return token;
  // console.log("갱신필요");
  return doRefreshWithLock();
};

const isAuthEndpoint = (url?: string) => {
  const path = url || '';
  return path.includes('/auth/refresh')
    || path.includes('/auth/login')
    || path.includes('/auth/student/find-id')
    || path.includes('/auth/student/reset-password')
    || path.includes('/auth/student/register')
    || path.includes('/auth/student/signup')
    || path.includes('/auth/student/email')
    || path.includes('/auth/student/check-login-id');
};

// 요청 인터셉터: 만료 체크 후 갱신, Authorization 헤더 설정
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (isAuthEndpoint(config.url)) {
      return config;
    }
    const validToken = await ensureValidToken();
    // console.log('validToken', validToken);
    if (validToken) {
      config.headers['Authorization'] = `Bearer ${validToken}`;
    }
    // logger.debug('API Request:', {
    //   method: config.method?.toUpperCase(),
    //   url: config.url,
    //   headers: config.headers,
    // });
    return config;
  },
  (error) => {
    logger.error('Request Error:', error);
    return Promise.reject(error);
  }
);

const logoutAndRedirect = () => {
  deleteAuthTokenCookie();
  deleteRefreshTokenStorage();
  window.location.href = '/';
};

// 응답 인터셉터: 401 시 갱신 시도 후 재요청
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // logger.debug('API Response:', {
    //   status: response.status,
    //   statusText: response.statusText,
    //   url: response.config.url,
    // });
    return response;
  },
  async (error) => {
    logger.error('Response Error:', error);
    const originalRequest = error.config;

    if (error.response?.status === 401 && !isAuthEndpoint(originalRequest?.url)) {
      // 원래 요청에 Authorization 헤더가 없었으면 비로그인 요청 → 리다이렉트하지 않음
      if (!originalRequest?.headers?.['Authorization']) {
        return Promise.reject(error);
      }
      logger.info('[401] 토큰 갱신 시도 (로그아웃 전 refresh 호출)');
      const token = await doRefreshWithLock();
      if (token) {
        logger.info('[401] 토큰 갱신 성공, 원본 요청 재시도');
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return apiClient.request(originalRequest);
      }
      logger.warn('[401] 토큰 갱신 실패 또는 리프레시 토큰 없음, 로그아웃 처리');
      logoutAndRedirect();
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      logger.warn('접근이 거부되었습니다.');
    }

    if (error.code === 'ERR_NETWORK') {
      logger.error('네트워크 오류가 발생했습니다.');
    }

    return Promise.reject(error);
  }
);

export default apiClient;
